import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
} from '@mui/material'
import { ContentCopy, CheckCircle, Lock } from '@mui/icons-material'

interface PasteInfo {
  content: string
  isEncrypted: boolean
  expiryTime?: string
}

export default function PasteView() {
  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pasteInfo, setPasteInfo] = useState<PasteInfo | null>(null)
  const [password, setPassword] = useState('')
  const [copied, setCopied] = useState(false)
  const [decrypting, setDecrypting] = useState(false)

  useEffect(() => {
    fetchPasteInfo()
  }, [id])

  const fetchPasteInfo = async () => {
    if (!id) return
    
    try {
      const response = await fetch(`/api/paste/${id}`, {
        method: 'GET',
        headers: {
          'Accept': 'text/plain',
        },
      })

      if (!response.ok) {
        if (response.status === 403) {
          setPasteInfo({ content: '', isEncrypted: true })
          setLoading(false)
          return
        }
        throw new Error('内容不存在或已过期')
      }

      const content = await response.text()
      setPasteInfo({ content, isEncrypted: false })
    } catch (error) {
      setError(error instanceof Error ? error.message : '加载失败')
    } finally {
      setLoading(false)
    }
  }

  const handleDecrypt = async () => {
    if (!password.trim() || !id) return

    setDecrypting(true)
    setError(null)
    
    try {
      const response = await fetch(`/api/paste/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${password}`,
          'Accept': 'text/plain',
        },
      })

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('密码错误')
        }
        throw new Error('获取内容失败')
      }

      const content = await response.text()
      setPasteInfo({ content, isEncrypted: false })
    } catch (error) {
      setError(error instanceof Error ? error.message : '解密失败')
    } finally {
      setDecrypting(false)
    }
  }

  const handleCopyContent = async () => {
    if (!pasteInfo?.content) return

    try {
      await navigator.clipboard.writeText(pasteInfo.content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('复制失败:', error)
    }
  }

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    )
  }

  if (pasteInfo?.isEncrypted) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Lock sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6">
                加密内容
              </Typography>
            </Box>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                type="password"
                label="请输入密码"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={decrypting}
              />
            </Box>
            <Button
              variant="contained"
              onClick={handleDecrypt}
              disabled={decrypting || !password.trim()}
            >
              {decrypting ? '解密中...' : '查看内容'}
            </Button>
          </Paper>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={copied ? <CheckCircle /> : <ContentCopy />}
              onClick={handleCopyContent}
            >
              {copied ? '已复制' : '复制内容'}
            </Button>
          </Box>
          <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {pasteInfo?.content}
            </pre>
          </Paper>
        </Paper>
      </Box>
    </Container>
  )
} 